import React from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import WalletOutlinedIcon from "@mui/icons-material/WalletOutlined";
import SecureConnected from "../components/SecureConnected";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import useScrollReveal from "../hooks/useScrollReveal";
import heroBg from "../assets/figma/hero-bg.png";
import extraIncome from "../assets/figma/extra-income.png";
import heroPerson from "../assets/figma/hero-person.png";
import secLogo from "../assets/partner-logos/SEC.png";
import amlcLogo from "../assets/partner-logos/AMLC.png";
import globeLogo from "../assets/partner-logos/Globe.png";
import cebuanaLogo from "../assets/partner-logos/Cebuana.png";
import finscoreLogo from "../assets/partner-logos/Finscore.png";
import netglobalLogo from "../assets/partner-logos/Netglobal.png";
import empoweringOne from "../assets/figma/empowering 1.png";
import empoweringTwo from "../assets/figma/Empowering 2.png";
import empoweringThree from "../assets/figma/Empowering 3.png";
import "./Home.css";

const partnerLogos = [
  { src: secLogo, alt: "Securities and Exchange Commission" },
  { src: amlcLogo, alt: "Anti-Money Laundering Council" },
  { src: globeLogo, alt: "Globe" },
  { src: cebuanaLogo, alt: "Cebuana Lhuillier" },
  { src: finscoreLogo, alt: "FinScore" },
  { src: netglobalLogo, alt: "NetGlobal Pay" },
];

const pathCards = [
  {
    icon: <GroupsOutlinedIcon />,
    title: "Become a Lender",
    subtitle: "Fund verified borrowers",
    bullets: ["Start from ₱500", "Set your own interest", "Chat directly"],
  },
  {
    icon: <BusinessRoundedIcon />,
    title: "Business",
    subtitle: "Business-focused financing",
    bullets: ["Fund your employees", "Custom loan terms", "Direct monitoring"],
  },
  {
    icon: <GroupsOutlinedIcon />,
    title: "Borrower",
    subtitle: "Access fast funding",
    bullets: ["Flexible terms", "Quick approval process", "Loans up to ₱500,000"],
  },
];

const journeyImages = [
  { src: empoweringOne, alt: "Personal lending feature preview" },
  { src: empoweringTwo, alt: "Online chat and encryption feature preview" },
  { src: empoweringThree, alt: "Repay easily via QRPh feature preview" },
];

const processSteps = [
  {
    icon: <VerifiedUserOutlinedIcon />,
    title: "Register & Verify",
    copy: "Complete KYC verification",
  },
  {
    icon: <PersonSearchOutlinedIcon />,
    title: "Browse & Match",
    copy: "Find the right loan match",
  },
  {
    icon: <SecurityOutlinedIcon />,
    title: "Secure Transaction",
    copy: "Disburse Loan securely",
  },
  {
    icon: <WalletOutlinedIcon />,
    title: "Earn & Repay",
    copy: "Fund or repay with ease",
  },
];

const processCards = [
  {
    title: "For Lenders",
    bullets: ["Support multiple borrowers", "Choose your lending criteria", "Fund distribution"],
  },
  {
    title: "For Businesses",
    bullets: ["Higher loan amounts", "Business-focused analysis", "Manage your loans"],
  },
  {
    title: "For Borrowers",
    bullets: ["Competitive interest rates", "Flexible loan terms", "Quick approval process"],
  },
];

const newsCards = [
  {
    tag: "Partnership",
    date: "October 26, 2026",
    title: "trilLOAN Partners with Major Banks for Enhanced Services",
  },
  {
    tag: "Technology",
    date: "October 26, 2026",
    title: "New Mobile App Features: Biometric Authentication",
  },
  {
    tag: "Milestone",
    date: "October 26, 2026",
    title: "Business Lending Program Reaches ₱1 Billion Milestone",
  },
];

const dealCards = [
  {
    tag: "For Borrowers",
    partner: "Globe",
    title: "First Loan Globe Rewards",
    copy: "Get Globe rewards for your first successful loan.",
    bullets: ["Free Globe load credits", "Exclusive Globe perks", "Valid for first-time borrowers"],
    action: "Download App to Apply",
  },
  {
    tag: "For Lenders",
    partner: "Cebuana",
    title: "Cebuana Credit Rewards",
    copy: "Earn rewards when you successfully fund loans via Cebuana.",
    bullets: ["Free Globe load credits", "Exclusive Globe perks", "Valid for first-time successful loan"],
    action: "Download App to Credit Loan",
  },
  {
    tag: "Referral Program",
    partner: "Icon",
    title: "Refer & Earn Globe Rewards",
    copy: "Refer up to 5 friends and earn Globe rewards for successful referrals.",
    bullets: ["Up to 5 successful referrals", "Exclusive Globe perks", "Bonus rewards at 5 referrals"],
    action: "Download App to Refer",
  },
];

function CheckList({ items }) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item}>
          <CheckCircleOutlineRoundedIcon />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function Home() {
  useScrollReveal();

  return (
    <main className="tl-home">
      <SiteHeader />

      <section className="home-hero" style={{ "--hero-bg": `url(${heroBg})` }}>
        <img src={heroPerson} alt="trilLOAN user viewing the app" />
        <div className="home-hero-copy">
          <span>Trusted Peer-to-Peer Platform</span>
          <img
            className="home-hero-title-art"
            src={extraIncome}
            alt="Kumita ng extra income. Maging digital lender na."
          />
          <a href="/partners">
            trilLOAN for Business
            <ArrowForwardRoundedIcon />
          </a>
        </div>
      </section>

      <section className="partner-section" aria-label="Partners">
        <div className="partner-logo-row">
          {partnerLogos.map((logo) => (
            <img src={logo.src} alt={logo.alt} key={logo.alt} />
          ))}
        </div>
      </section>

      <section className="growth-section section-blue tl-reveal" id="services">
        <div className="home-section-heading">
          <h2>Your Path to Financial Growth</h2>
          <p>
            Whether you're looking to provide funding, access capital, or finance
            your business, trilLOAN offers tailored solutions.
          </p>
        </div>
        <div className="growth-card-grid">
          {pathCards.map((card, index) => (
            <article className={`growth-card tl-reveal tl-reveal-delay-${index % 3}`} key={card.title}>
              <span className="circle-icon">{card.icon}</span>
              <h3>{card.title}</h3>
              <p>{card.subtitle}</p>
              <CheckList items={card.bullets} />
              <a href="#download">Download app to register</a>
            </article>
          ))}
        </div>
        <div className="download-prompt">
          <p>Ready to get started?</p>
          <a href="#download">
            <CloudDownloadOutlinedIcon />
            Download the trilLOAN App
          </a>
        </div>
      </section>

      <section className="journey-section tl-reveal">
        <div className="home-section-heading">
          <h2>Empowering Your Financial Journey</h2>
          <p>
            Discover the core features that make trilLOAN the leading personal lending
            platform for secure and efficient financial connections.
          </p>
        </div>
        <div className="journey-image-row">
          {journeyImages.map((image, index) => (
            <img
              className={`tl-reveal tl-reveal-delay-${index % 3}`}
              src={image.src}
              alt={image.alt}
              key={image.alt}
            />
          ))}
        </div>
      </section>

      <section className="works-section section-blue tl-reveal">
        <div className="home-section-heading">
          <h2>How Peer-to-Peer Loans Work</h2>
        </div>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <article className={`process-step tl-reveal tl-reveal-delay-${index % 3}`} key={step.title}>
              <span className="circle-icon">{step.icon}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
        <div className="process-card-grid">
          {processCards.map((card, index) => (
            <article className={`process-card tl-reveal tl-reveal-delay-${index % 3}`} key={card.title}>
              <h3>{card.title}</h3>
              <ul>
                {card.bullets.map((item) => (
                  <li key={item}>
                    <ArrowForwardRoundedIcon />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <SecureConnected id="download" />

      <SiteFooter />
    </main>
  );
}
